import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://cssbuy.biz";

const etapas = [
  { step: "1", title: "Acesse o CSSBuy Spreadsheet", desc: "Comece acessando nossa planilha CSSBuy com milhares de links verificados e organizados por categoria. Esta é sua porta de entrada para os melhores produtos disponíveis no mercado internacional." },
  { step: "2", title: "Escolha sua Categoria", desc: "Navegue pelas categorias como Shoes, T-Shirts, Jackets, Accessories e encontre os produtos que mais combinam com seu estilo e orçamento. Cada categoria contém dezenas de vendedores verificados." },
  { step: "3", title: "Copie o Link e Cole no CSSBuy", desc: "Ao encontrar o produto desejado, copie o link e cole na barra de busca do CSSBuy. O sistema processará automaticamente as informações do produto para você." },
  { step: "4", title: "Solicite QC Photos", desc: "Após o produto chegar ao armazém, solicite as fotos de controle de qualidade. Esta etapa é fundamental para garantir que o produto corresponde exatamente ao que você espera." },
  { step: "5", title: "Escolha o Envio e Receba", desc: "Selecione a melhor opção de envio para o Brasil e acompanhe seu pedido até a entrega. Nossa planilha inclui dicas sobre as melhores linhas de envio para cada situação." },
];

const linksInternos = [
  { label: "Guia Completo CSSBuy Spreadsheet 2026", to: "/cssbuy-spreadsheet-guia-completo" },
  { label: "Planilha CSSBuy: Guia Definitivo", to: "/planilha-cssbuy" },
  { label: "CSSBuy Yupoo: Melhores Álbuns", to: "/cssbuy-yupoo" },
  { label: "CSSBuy Review 2026", to: "/cssbuy-review" },
  { label: "Como Usar o CSSBuy", to: "/como-usar-cssbuy" },
  { label: "CSSBuy vs Pandabuy", to: "/cssbuy-vs-pandabuy" },
  { label: "Melhores Links CSSBuy", to: "/melhores-links" },
  { label: "Guia de Envio para o Brasil", to: "/guia-envio" },
  { label: "Compras Seguras com CSSBuy", to: "/compras-seguras" },
  { label: "CSSBuy Spreadsheet Essencial", to: "/cssbuy-spreadsheet" },
];

export default function GuiaDeComprasPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context":"https://schema.org","@type":"Article",
        "headline":"Guia de Compras CSSBuy: Tudo que Você Precisa Saber para Compras Internacionais",
        "url": `${SITE_URL}/guia-de-compras`,
        "author":{"@type":"Person","name":"Equipe CSSBuy Brasil"},
        "datePublished":"2026-06-24","dateModified":"2026-06-27",
        "publisher":{"@type":"Organization","name":"CSSBuy Brasil","url": SITE_URL},
        "inLanguage":"pt-BR"
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context":"https://schema.org","@type":"BreadcrumbList",
        "itemListElement":[
          {"@type":"ListItem","position":1,"name":"Início","item": `${SITE_URL}/`},
          {"@type":"ListItem","position":2,"name":"Guia de Compras","item": `${SITE_URL}/guia-de-compras`}
        ]
      })}} />
      <Navbar />
      <main>
        <article className="pt-24 pb-16 md:pt-28 md:pb-24">
          <div className="max-w-4xl mx-auto px-4 md:px-8 mb-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400">
              <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">Início</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">Guia de Compras</span>
            </nav>
          </div>
          <header className="max-w-4xl mx-auto px-4 md:px-8 mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4 whitespace-nowrap">Guia de Compras</span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">Guia de Compras CSSBuy: Tudo que Você Precisa Saber</h1>
            <p className="mt-4 text-foreground-500 text-base md:text-lg leading-relaxed max-w-2xl">
              O <strong>Guia de Compras CSSBuy</strong> mais completo em português. Aprenda cada etapa do processo, desde a escolha do produto na <strong>planilha CSSBuy</strong> até o recebimento no Brasil com segurança e economia.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-foreground-400">
              <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-user-line"></i>Equipe CSSBuy Brasil</span>
              <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-calendar-line"></i>Atualizado: Junho 2026</span>
              <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-time-line"></i>10 min de leitura</span>
            </div>
            <div className="mt-8 rounded-2xl overflow-hidden h-[300px] md:h-[420px]">
              <img src="https://readdy.ai/api/search-image?query=Comprehensive%20shopping%20guide%20concept%20with%20laptop%20showing%20steps%20and%20fashion%20items%20arranged%20on%20warm%20minimalist%20desk%20soft%20natural%20lighting%20editorial%20style%20clean%20composition%20neutral%20cream%20background%20complete%20guide%20aesthetic%20instructional%20theme&width=1200&height=600&seq=art-page-guia-de-compras&orientation=landscape" alt="Guia de Compras CSSBuy" className="w-full h-full object-cover" />
            </div>
          </header>
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <div className="text-foreground-700 leading-relaxed space-y-8">
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Bem-vindo ao Guia de Compras CSSBuy</h2>
                <p className="text-base leading-relaxed">
                  Se você quer aprender a comprar produtos internacionais com segurança, economia e praticidade, este <strong>Guia de Compras CSSBuy</strong> foi feito para você. Reunimos todo o conhecimento necessário para transformar você em um comprador experiente, utilizando nossa <strong>planilha CSSBuy</strong> como ferramenta principal.
                </p>
              </section>
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Etapas do Guia de Compras</h2>
                <div className="space-y-5">
                  {etapas.map((item) => (
                    <div key={item.step} className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-700 font-bold text-sm">{item.step}</span>
                      </div>
                      <div>
                        <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h4>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Artigos do Guia de Compras</h2>
                <p className="text-base leading-relaxed mb-4">Explore nossos artigos detalhados para dominar cada aspecto das compras com CSSBuy:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {linksInternos.map((link) => (
                    <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-all cursor-pointer">
                      <i className="ri-article-line text-primary-500 flex-shrink-0"></i>
                      <span>{link.label}</span>
                    </Link>
                  ))}
                </div>
              </section>
              <section>
                <div className="bg-foreground-900 rounded-3xl p-8 md:p-10 text-center">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">Comece Suas Compras Agora</h2>
                  <p className="text-background-50/60 text-sm md:text-base max-w-lg mx-auto mb-6">Com nosso guia de compras completo e a planilha CSSBuy, você está pronto para fazer compras internacionais com total confiança e segurança.</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">cssbuy sheet<i className="ri-arrow-right-up-line ml-2"></i></a>
                    <Link to="/" className="px-8 py-3.5 rounded-full border border-background-50/20 text-background-50/70 text-sm font-medium hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer">← Voltar ao Início</Link>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}